import { create } from "zustand"
import { persist, createJSONStorage } from "zustand/middleware"
import type { Artikal } from "../api/catalog"

export interface CartItem {
    sifra: string
    nazivArtikla: string
    cijena: number
    kolicina: number
    jedMj: string
    napomena?: string
    monitor?: number
}

export interface Narudzba {
    id: string                  // unique ID generisan lokalno
    broj: number                // redni broj #1, #2, #3...
    stol: string | null         // opciono ime stola
    items: CartItem[]
    kreirano: number            // timestamp
    izmjenjeno: number          // timestamp
}

interface OrderState {
    narudzbe: Narudzba[]
    aktivnaNarudzbaId: string | null

    // Upravljanje narud�bama
    kreirajNarudzbu: (stol?: string | null) => string
    obrisiNarudzbu: (id: string) => void
    preimenuj: (id: string, stol: string | null) => void
    setAktivna: (id: string | null) => void
    ocistiSve: () => void

    // Operacije nad AKTIVNOM narud�bom
    addItem: (artikal: Artikal, kolicina?: number) => void
    updateQuantity: (sifra: string, kolicina: number) => void
    updateNapomena: (sifra: string, napomena: string) => void
    removeItem: (sifra: string) => void
    clearAktivnu: () => void

    // Getteri
    getAktivnaNarudzba: () => Narudzba | null
    getItems: () => CartItem[]
    total: () => number
    totalCount: () => number
}

function generateId(): string {
    return `n_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

function modifyAktivna(state: OrderState, updater: (n: Narudzba) => Narudzba): OrderState {
    if (!state.aktivnaNarudzbaId) return state
    return {
        ...state,
        narudzbe: state.narudzbe.map(n =>
            n.id === state.aktivnaNarudzbaId
                ? { ...updater(n), izmjenjeno: Date.now() }
                : n
        )
    }
}

export const useOrderStore = create<OrderState>()(
    persist(
        (set, get) => ({
            narudzbe: [],
            aktivnaNarudzbaId: null,

            kreirajNarudzbu: (stol = null) => {
                const id = generateId()
                const narudzbe = get().narudzbe
                const sledeciBroj = narudzbe.length === 0
                    ? 1
                    : Math.max(...narudzbe.map(n => n.broj)) + 1
                const nova: Narudzba = {
                    id,
                    broj: sledeciBroj,
                    stol: stol?.trim() || null,
                    items: [],
                    kreirano: Date.now(),
                    izmjenjeno: Date.now()
                }
                set({
                    narudzbe: [...narudzbe, nova],
                    aktivnaNarudzbaId: id
                })
                return id
            },

            obrisiNarudzbu: (id) => set((state) => {
                const filtered = state.narudzbe.filter(n => n.id !== id)
                return {
                    narudzbe: filtered,
                    aktivnaNarudzbaId: state.aktivnaNarudzbaId === id ? null : state.aktivnaNarudzbaId
                }
            }),

            preimenuj: (id, stol) => set((state) => ({
                narudzbe: state.narudzbe.map(n =>
                    n.id === id
                        ? { ...n, stol: stol?.trim() || null, izmjenjeno: Date.now() }
                        : n
                )
            })),

            setAktivna: (id) => set({ aktivnaNarudzbaId: id }),

            ocistiSve: () => set({ narudzbe: [], aktivnaNarudzbaId: null }),

            addItem: (artikal, kolicina = 1) => set((state) => modifyAktivna(state, (n) => {
                const existing = n.items.find(i => i.sifra === artikal.sifra)
                if (existing) {
                    return {
                        ...n,
                        items: n.items.map(i =>
                            i.sifra === artikal.sifra
                                ? { ...i, kolicina: i.kolicina + kolicina }
                                : i
                        )
                    }
                }
                return {
                    ...n,
                    items: [...n.items, {
                        sifra: artikal.sifra,
                        nazivArtikla: artikal.nazivArtikla,
                        cijena: artikal.cijena,
                        kolicina,
                        jedMj: artikal.jedMj || "KOM",
                        monitor: artikal.monitor
                    }]
                }
            })),

            updateQuantity: (sifra, kolicina) => set((state) => modifyAktivna(state, (n) => ({
                ...n,
                items: kolicina <= 0
                    ? n.items.filter(i => i.sifra !== sifra)
                    : n.items.map(i => i.sifra === sifra ? { ...i, kolicina } : i)
            }))),

            updateNapomena: (sifra, napomena) => set((state) => modifyAktivna(state, (n) => ({
                ...n,
                items: n.items.map(i => i.sifra === sifra ? { ...i, napomena } : i)
            }))),

            removeItem: (sifra) => set((state) => modifyAktivna(state, (n) => ({
                ...n,
                items: n.items.filter(i => i.sifra !== sifra)
            }))),

            clearAktivnu: () => set((state) => {
                if (!state.aktivnaNarudzbaId) return state
                // Bri�e aktivnu narud�bu kompletno iz liste
                return {
                    narudzbe: state.narudzbe.filter(n => n.id !== state.aktivnaNarudzbaId),
                    aktivnaNarudzbaId: null
                }
            }),

            getAktivnaNarudzba: () => {
                const state = get()
                if (!state.aktivnaNarudzbaId) return null
                return state.narudzbe.find(n => n.id === state.aktivnaNarudzbaId) || null
            },

            getItems: () => {
                const aktivna = get().getAktivnaNarudzba()
                return aktivna?.items || []
            },

            total: () => {
                const items = get().getItems()
                return items.reduce((sum, i) => sum + i.cijena * i.kolicina, 0)
            },

            totalCount: () => {
                const items = get().getItems()
                return items.reduce((sum, i) => sum + i.kolicina, 0)
            }
        }),
        {
            name: "sadeo-cart",
            storage: createJSONStorage(() => sessionStorage),
            partialize: (state) => ({
                narudzbe: state.narudzbe,
                aktivnaNarudzbaId: state.aktivnaNarudzbaId
            })
        }
    )
)